import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { copyTasks } from 'bundles/apex/playbooks/config/store/playbooks/actions';

import Selects from './selects';
import Button from 'shared/button';
import Errors from 'components/form/errors';
import ManagedInput from 'components/form/managed_input';
import Modal from 'components/modal';

import './styles.scss';
import TasksTable from './table';
import EmptyState from './empty_state';
import { getOrderedStages } from 'bundles/apex/helpers/stages';

export function CopyTaskModal({ copyTasks, dealType, dealTypeOptions, playbooks, show, stage, stages, onHide }) {
  const orderedStages = getOrderedStages(stages);

  const [errors, setErrors] = useState();
  const [saving, setSaving] = useState();
  const [fromDealType, setFromDealType] = useState(dealType);
  const [fromStage, setFromStage] = useState(stage);
  const [toDealType, setToDealType] = useState(dealTypeOptions[0].value);
  const [toStage, setToStage] = useState(orderedStages[0]);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [createTasks, setCreateTasks] = useState();

  const getTasks = (dealType, stage) => {
    const playbook = playbooks.find(playbook => playbook.dealType === dealType);

    if (playbook) {
      return playbook.tasksByStage[stage.key] || [];
    }

    return [];
  };

  const tasks = useMemo(() => getTasks(fromDealType, fromStage), [fromDealType, fromStage, playbooks]);

  const reset = () => {
    const newTasks = getTasks(dealType, stage);
    setErrors();
    setSaving();
    setFromDealType(dealType);
    setFromStage(stage);
    setToDealType(newTasks.length === 0 ? dealType : dealTypeOptions[0].value);
    setToStage(newTasks.length === 0 ? stage : orderedStages[0]);
    setSelectedTaskIds([]);
    setCreateTasks();
  };

  const handleFromDealTypeChange = (dealType) => {
    setFromDealType(dealType);
    setSelectedTaskIds([]);
  };
  const handleFromStageChange = (stage) => {
    setFromStage(stage);
    setSelectedTaskIds([]);
  };

  const handleSelectAll = () => {
    if (selectedTaskIds.length === 0) {
      setSelectedTaskIds(tasks.map(task => task.id));
    } else {
      setSelectedTaskIds([]);
    }
  };
  const handleSelectTask = (task) => {
    if (selectedTaskIds.indexOf(task.id) === -1) {
      setSelectedTaskIds([...selectedTaskIds, task.id]);
    } else {
      setSelectedTaskIds(selectedTaskIds.filter(id => id !== task.id));
    }
  };

  async function handleSave() {
    setSaving(true);

    try {
      await copyTasks(
        tasks.filter(task => selectedTaskIds.indexOf(task.id) !== -1),
        toDealType,
        toStage.key,
        createTasks
      );
      onHide();
    } catch (errors) {
      setErrors(errors);
      setSaving(false);
    }
  }


  return (
    <Modal className="playbook-tasks-copy-modal visible-overflow" show={show} onHide={onHide} onShow={reset}>
      <Modal.Header closeButton>Copy Tasks</Modal.Header>

      <Modal.Body>
        <Errors errors={errors}/>
        <Selects
          dealType={fromDealType}
          dealTypeOptions={dealTypeOptions}
          label="Copy from"
          stage={fromStage}
          stages={stages}
          onDealTypeChange={handleFromDealTypeChange}
          onStageChange={handleFromStageChange}
        />
        {tasks.length > 0 ? (
          <React.Fragment>
            <TasksTable
              selectedTaskIds={selectedTaskIds}
              tasks={tasks}
              onSelectAll={handleSelectAll}
              onSelectTask={handleSelectTask}
            />
          </React.Fragment>
        ) :
          <EmptyState />
        }
        <Selects
          dealType={toDealType}
          dealTypeOptions={dealTypeOptions}
          label="Copy to"
          stage={toStage}
          stages={stages}
          onDealTypeChange={setToDealType}
          onStageChange={setToStage}
        />
      </Modal.Body>

      <Modal.Footer>
        <div className="playbook-tasks-copy-modal-footer">
          <ManagedInput
            name="createTasks"
            type="checkbox"
            value={createTasks}
            onValueChange={setCreateTasks}
          >
            Add task to existing deals once copied
          </ManagedInput>
          <div>
            <Button.Cancel name="cancel" onClick={onHide} />
            <Button.Primary
              disabled={selectedTaskIds.length === 0 || saving}
              name="save"
              onClick={handleSave}
            >
              Copy
            </Button.Primary>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

CopyTaskModal.propTypes = {
  copyTasks: PropTypes.func.isRequired,
  dealType: PropTypes.string.isRequired,
  dealTypeOptions: PropTypes.array.isRequired,
  playbooks: PropTypes.array.isRequired,
  show: PropTypes.bool,
  stage: PropTypes.shape({
    key: PropTypes.string.isRequired,
  }).isRequired,
  stages: PropTypes.object.isRequired,
  onHide: PropTypes.func.isRequired,
};

const mapStateToProps = ({ dealType, dealTypeOptions, playbooks, stage, stages }) => {
  return {
    dealType,
    dealTypeOptions,
    playbooks,
    stage,
    stages,
  };
};

const mapDispatchToProps = {
  copyTasks
};

export default connect(mapStateToProps, mapDispatchToProps)(CopyTaskModal);
